import {
  type AlertType,
  type AppointmentStatus,
  type ClassificationDefinition,
  type ConsentStatusType,
  type ConsentType,
  type InstallationQuotationStatus,
  type InstallationType,
  type NbaType,
  type Opportunity,
  type OpportunityType,
  type Order,
  type ParentTaskType,
  type PrecallReason,
  type PrecallResult,
  type PreferredContactMethod,
  type ResponseStatus,
  type SubTaskType,
  type hfb,
} from "@repo/api";
import { t } from "@repo/translation";
import { type Values, getClassificationName } from "@repo/utils";

import { type TaskConfig } from "../settings/SettingsProvider";
import { getTaskConfig } from "../settings/taskConfiguration";

// TODO: Refactor
// This is not ideal to have in the UI package.)

const translationPrefixs = [
  "action.",
  "activity.",
  "alert.",
  "appointment_status.",
  "appointment_type.",
  "classification_reason.",
  "classification.",
  "consent.",
  "customer_consent.",
  "customer_type.",
  "drawing.",
  "installation_quotation_status.",
  "interaction.",
  "label.",
  "language.",
  "location_type.",
  "nba.",
  "order_method.",
  "order_status.",
  "order.",
  "payment_status.",
  "precall_detail.",
  "prompt.",
  "reason_code.",
  "response_status.",
  "service_area.",
  "services.",
  "status_change_reason.",
  "status.",
  "tag.",
  "tags.",
  "tour.",
  "type.",
];

export function translateAsKey(text?: string) {
  if (!text) {
    return "-";
  }
  return t(text);
}

export function translateProjectStatus(status?: Opportunity["status"]) {
  if (!status) {
    return "-";
  }
  return t(`status.${status.toLowerCase()}`);
}

export function translateServiceStatus(status?: string) {
  if (!status) {
    return "-";
  }

  return t(`service_status.${status}`);
}

export function translateOpportunityType(type?: OpportunityType) {
  if (!type) {
    return "-";
  }

  const typeMap: Record<OpportunityType, string> = {
    "Sales Opportunity": t("type.sales_project"),
    "Blocked Order": t("type.blocked_order"),
    Boka: t("type.boka"),
    ENR: "ENR",
    Quotation: t("label.quotation"),
    "Assistance Request": t("type.assistance_request"),
  };

  return typeMap[type];
}

export function translateServices(service?: string) {
  if (!service) {
    return undefined;
  }

  return t(`services.${service}`);
}

export function translateInstallationQuotationExists(exists?: boolean) {
  if (exists === undefined) {
    return;
  }

  return exists
    ? t("label.have_installation_quotation")
    : t("label.no_installation_quotation");
}

export function translateInstallationQuotationStatus(
  status?: InstallationQuotationStatus
) {
  if (!status) {
    return "-";
  }

  return t(`installation_quotation_status.${status.toLowerCase()}`);
}

export function translateLocationType(locationType?: string) {
  if (!locationType) {
    return "-";
  }

  return t(`location_type.${locationType.toUpperCase()}`);
}

export function translatePaymentStatus(paymentStatus?: string) {
  if (!paymentStatus) {
    return "-";
  }

  return t(`payment_status.${paymentStatus}`);
}

export function translateClassification(
  classification?: number | ClassificationDefinition
) {
  if (!classification) {
    return "-";
  }

  if (typeof classification === "number") {
    return t(
      `classification.${getClassificationName(classification).toLowerCase()}`
    );
  }

  return t(`classification.${String(classification).toLowerCase()}`);
}

export function translateClassificationReason(reason?: string) {
  if (!reason) {
    return null;
  }

  return reason === "OTHER"
    ? t("label.other_reason")
    : t(`classification_reason.${reason}`);
}

export function translateAppointmentStatus(status?: string) {
  if (!status) {
    return "-";
  }

  return t(`appointment_status.${status}`);
}
export function translateAppointmentType(type: string) {
  return t(`appointment_type.${type}`);
}

export function translateLabel(text?: string) {
  if (!text?.startsWith("label.") || text?.includes(" ")) {
    return text ?? "";
  }
  return t(text);
}

export function translateCustomerType(id: string) {
  if (id === "Individual") {
    return t("label.individual");
  } else if (id === "Business") {
    return t("label.business");
  }

  return id;
}

export function translateQuotationStatus(quotationStatus?: string) {
  if (!quotationStatus) {
    return "-";
  }

  return t(`label.${quotationStatus}`);
}

export function translateAddPrefix(
  value?: string,
  prefix?: string,
  lowercase?: boolean
) {
  if (!value) {
    return "-";
  }

  const formatPrefix = prefix ? `${prefix}.` : "";

  return t(`${formatPrefix}${lowercase ? value.toLowerCase() : value}`);
}

export function translateTaskType(
  language?: string,
  parentTaskType?: ParentTaskType,
  subTaskType?: SubTaskType,
  taskConfigs?: TaskConfig[],
  getTranslationKey?: boolean
) {
  const taskConfig = getTaskConfig(parentTaskType, subTaskType, taskConfigs);

  if (subTaskType) {
    const translation = taskConfig?.subTaskConfig?.translations?.find(
      (el) => el.language_key === language
    )?.translation;

    const translationKey = taskConfig?.subTaskConfig?.translationKey;

    return getTranslationKey
      ? (translationKey ?? translation ?? subTaskType)
      : (translation ?? (translationKey ? t(translationKey) : subTaskType));
  }

  if (parentTaskType) {
    const translation = taskConfig?.parentTaskConfig?.translations?.find(
      (el) => el.language_key === language
    )?.translation;
    const translationKey = taskConfig?.parentTaskConfig?.translationKey;
    return getTranslationKey
      ? (translationKey ?? translation ?? parentTaskType)
      : (translation ?? (translationKey ? t(translationKey) : parentTaskType));
  }

  return "-";
}

export function translateOrderType(type?: Order["orderType"]) {
  if (!type) {
    return "-";
  }
  return t(`order.type.${type.toLowerCase()}`);
}

export function translateOrderStatus(status?: Order["orderStatus"]) {
  if (!status) {
    return "-";
  }
  return t(`order_status.${status.toLowerCase()}`);
}

export function translateOrderMethod(method?: string) {
  if (!method) {
    return "-";
  }
  return t(`order_method.${method}`);
}

export function translateOrderPaymentStatus(status?: string) {
  if (!status) {
    return "-";
  }

  return t(`payment_status.${status}`);
}

export function translateResponseStatus(status?: ResponseStatus) {
  if (!status) {
    return "-";
  }
  return t(`response_status.${status}`);
}

// Translate HFB to name. hfb07 => "Kitchen"
export function translateHFBName(hfbKey?: hfb) {
  if (!hfbKey) {
    return null;
  }
  return t(hfbKey);
}

// Translate HFB to key - name. hfb07 => "hfb07 - Kitchen"
export function translateHFB(hfbKey?: hfb) {
  if (!hfbKey) {
    return "-";
  }

  if (hfbKey.startsWith("hfb")) {
    return `${hfbKey} - ${t(hfbKey)}`;
  }
  return t(hfbKey);
}

export const sortTaskTranslationKeys = (
  translate: (key: string) => string,
  translationKeys: string[]
) => {
  return translationKeys
    .map((key) => {
      const translation = translate(key);
      return {
        label: translation ?? key,
        key,
      };
    })
    .sort(({ label: a }, { label: b }) => a.localeCompare(b));
};

export function translatePrecallResult(result?: PrecallResult) {
  if (!result) {
    return null;
  }
  return t(`precall_result.${result}`);
}

export function translatePrecallReason(reason?: PrecallReason) {
  if (!reason) {
    return null;
  }
  return reason === "OTHER"
    ? t("label.other_reason")
    : t(`precall_reason.${reason}`);
}

export function translateKey(text: string) {
  if (text && translationPrefixs.find((prefix) => text?.startsWith(prefix))) {
    return t(text);
  }
  return text;
}

export function translateQuestion(question?: string) {
  if (question?.startsWith("question.")) {
    return t(question);
  }
  return question;
}

export function translateAppointmentStatusChange(
  status: Partial<AppointmentStatus>
) {
  return t(`prompt.appointment_${status.toLocaleLowerCase()}`);
}

export function translateNbaType(nbaType: NbaType) {
  return t(`nba.${nbaType.toLowerCase()}`);
}

export function translateAlerts(alertType?: AlertType) {
  if (!alertType) {
    return "-";
  }
  return t(`alert.${alertType?.toLowerCase()}`);
}

export function translateLanguage(language: string) {
  return t(`language.${language}`);
}
export function translateContactMethod(
  contactMethod?: Values<typeof PreferredContactMethod>
) {
  if (contactMethod === "PHONE") {
    return t("label.phone");
  } else if (contactMethod === "EMAIL") {
    return t("label.email");
  }
  return undefined;
}

export function translateConsentMessageResult(
  type: "EMAIL" | "MOBILE",
  success?: boolean,
  description?: boolean,
  contactDetail?: string
) {
  return t(
    `prompt.${type === "EMAIL" ? "email" : "sms"}_sent_${
      success ? "success" : "error"
    }${description ? "_description" : ""}`,
    description ? { contactDetail } : undefined
  );
}

export function translateConsentType(consentType: ConsentType) {
  return t(`consent.${consentType}`);
}

export function translateConsentStatus(status: ConsentStatusType) {
  return t(`prompt.consent_${status.toLowerCase()}`);
}

export function translateConsentFilterStatus(
  value?: string
): string | undefined {
  let status;

  switch (value) {
    case "yes":
      status = t("label.yes");
      break;
    case "no":
      status = t("label.no");
      break;
    case "pending":
      status = t("label.pending");
  }

  return status;
}

// If key exists, use key. Otherwise use source value
export function translateProjectSource(source?: string) {
  if (!source) {
    return "-";
  }
  return t(`label.${source.toLowerCase()}`).includes("label.")
    ? source
    : t(`label.${source.toLowerCase()}`);
}

export function translateDrawingStatusCode(statusCode: string) {
  return t(`drawing.status_code.${statusCode}`);
}

// shiftType: WORK, BREAK, etc.
export function translateScheduleShiftType(shiftType?: string | null) {
  if (!shiftType) {
    return "-";
  }
  return t(`label.${shiftType.toLowerCase()}`);
}

export const translateStage = (stage: string) => {
  return t(`stage.${stage}`);
};

export const translateStageAction = (action: string) => {
  return t(`stage_action.${action}`);
};

export const translateStageActionDescription = (action: string) => {
  return t(`stage_action.description.${action}`);
};

export const translateInstallationType = (
  installationType?: InstallationType
) => {
  if (!installationType) {
    return "-";
  }
  return t(`
    ${installationType.toLowerCase()}`);
};
